import { createAsyncThunk } from '@reduxjs/toolkit';
import { http } from '@core';
import type { ShowInfoByIdResponse, VenueInfoResponse } from '../models/show.interface';
import { AppRoutes } from '@core';
import { setSelectedDate } from './slice';
import { urlParamsDataSelectors } from '../../seatSelection/store/selectors';
import type { RootState } from '@core';
import { formatTimeSlots } from '../utils/time-slots';

/**
 * Получение информации об show
 */

export const getShowInfo = createAsyncThunk<ShowInfoByIdResponse, { showId: number }>(
  'dateSelection/getShowInfo',
  async ({ showId }, { rejectWithValue }) => {
    try {
      const response = await http.get<ShowInfoByIdResponse>(`/api/v1/catalog/shows/${showId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching show info:', error);
      return rejectWithValue('Не удалось загрузить информацию о событии');
    }
  }
);

/**
 * Получение информации месте проведения*/

export const getVenueInfo = createAsyncThunk<
  VenueInfoResponse,
  { venueId: number; showId: number }
>(
  'dateSelection/getVenueInfo',
  async ({ venueId, showId }, { rejectWithValue }) => {
    try {
      const response = await http.get<VenueInfoResponse>(
        `/api/v1/catalog/${showId}/venues/${venueId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching venue info:', error);
      return rejectWithValue('Не удалось загрузить информацию о месте проведения');
    }
  }
);

/**
 * Комбинированное действие для получения информации о шоу и месте проведения
 */
export const fetchShowAndVenueInfo = createAsyncThunk<
  ShowInfoByIdResponse,
  { showId: number; step?: (typeof AppRoutes)[keyof typeof AppRoutes] }
>(
  'dateSelection/fetchShowAndVenueInfo',
  async ({ showId, step }, { dispatch, rejectWithValue, getState }) => {
    try {
      // Получаем информацию о шоу
      const showInfoResult = await dispatch(getShowInfo({ showId })).unwrap();

      // Получаем информацию о месте проведения, если доступен venueId
      if (showInfoResult?.events?.[0]?.venueId) {
        dispatch(
          getVenueInfo({
            venueId: showInfoResult.events[0].venueId,
            showId,
          })
        );
      }

      if (step === AppRoutes.SEATS_SELECTION) {
        const urlParamsData = urlParamsDataSelectors(getState() as RootState);
        const date = formatTimeSlots(showInfoResult.events, { eventId: urlParamsData.eventsIds });
        dispatch(
          setSelectedDate({
            date: date[0].date,
            time: date[0].time,
            eventId: date[0].eventId,
          })
        );
      }
      return showInfoResult;
    } catch (error) {
      return rejectWithValue('Не удалось загрузить информацию о событии и месте проведения');
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      return !state.dateSelection.isLoading;
    },
  }
);
